import type {Bilingue} from '../i18n/langue';
import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import type {ContentSample, ContentTag} from '../services/bugSynthesisService';

/** Nom lisible d'un tag de contenu, une fois son préfixe de source retiré. */
const TAG_LABELS: Record<string, Bilingue> = {
    method: {fr: 'Méthode', en: 'Method'},
    method_ref: {fr: 'Référence', en: 'Reference'},
    lesson: {fr: 'Leçon', en: 'Lesson'},
    number: {fr: 'Leçon', en: 'Lesson'},
    sentence: {fr: 'Phrase', en: 'Sentence'},
    section: {fr: 'Section', en: 'Section'},
    is_revision: {fr: 'Révision', en: 'Revision'},
    matches: {fr: 'Défaut', en: 'Fault'},
    load_error: {fr: 'Cause', en: 'Cause'},
};

/** Sections d'une leçon, du nom technique (`LessonSection.name`) au libellé. */
const SECTION_LABELS: Record<string, Bilingue> = {
    decouverte: {fr: 'Découverte', en: 'Discovery'},
    etude: {fr: 'Étude', en: 'Study'},
    motsNouveaux: {fr: 'Mots nouveaux', en: 'New words'},
    traduisez: {fr: 'Traduisez', en: 'Translate'},
    completezPhrase: {fr: 'Complétez la phrase', en: 'Fill in the sentence'},
    parlez: {fr: 'Parlez', en: 'Speak'},
    plus: {fr: 'Plus', en: 'More'},
    revision: {fr: 'Révision', en: 'Revision'},
    calligraphie: {fr: 'Calligraphie', en: 'Calligraphy'},
    progression: {fr: 'Bilan de progression', en: 'Progress review'},
    plusEmbeddedTraduction: {fr: 'Traduisez (dans Plus)', en: 'Translate (in More)'},
    plusEmbeddedCompletez: {fr: 'Complétez la phrase (dans Plus)', en: 'Fill in the sentence (in More)'},
    unknown: {fr: 'section inconnue', en: 'unknown section'},
};

const MATCH_LABELS: Record<string, Bilingue> = {
    rightAnswer: {fr: 'une mauvaise réponse répète la bonne', en: 'a wrong answer repeats the right one'},
    previousWrong: {fr: 'une mauvaise réponse en répète une autre', en: 'a wrong answer repeats another'},
    mixed: {fr: 'les deux à la fois', en: 'both at once'},
};

/** Le préfixe dit d'où vient le tag (`qcm.`, `lesson.`), pas ce qu'il désigne. */
function shortKey(key: string): string {
    return key.replace(/^(qcm|lesson)\./, '');
}

/** Le traducteur est passé en argument : ceci n'est pas un composant, et un
 *  crochet ne s'appelle pas ailleurs que dans un composant. */
function valueLabel(traduire: (texte?: Bilingue) => string, key: string, value: string): string {
    const short = shortKey(key);
    if (short === 'section') return traduire(SECTION_LABELS[value]) || value;
    if (short === 'matches') return traduire(MATCH_LABELS[value]) || value;
    if (short === 'is_revision') return value === 'true' ? 'oui' : 'non';
    return value;
}

interface ContentTagsProps {
    tags?: ContentTag[];
    /** Les comptes n'ont de sens que sur un candidat vivant : sur un bug promu,
     *  ce sont des chiffres figés au moment du rattachement. */
    showCounts?: boolean;
}

/**
 * Quel contenu est concerné : méthode, leçon, section, phrase.
 *
 * Sans ça, une issue sur 178 utilisateurs ne dit pas quoi corriger — et c'est
 * la même question sur un candidat que sur un bug déjà promu.
 */
/**
 * Contenu rapporté par un seul événement.
 *
 * Affiché quand les tags ne disent rien : les données additionnelles de Sentry
 * ne sont pas indexées, donc seul le dernier événement est lisible à faible
 * coût. La mention « dernier événement » est là pour qu'on ne prenne pas cet
 * échantillon pour la liste complète — c'est le relevé qui la donne.
 */
export function ContentSampleLine({sample}: {sample?: ContentSample | null}) {
    const t = useTraduire();
    if (!sample) return null;
    const parts = [
        sample.method + (sample.ref ? ` (réf. ${sample.ref})` : ''),
        sample.lesson ? `leçon ${sample.lesson}${sample.isRevision ? ' (révision)' : ''}` : null,
        sample.file ? `fichier attendu ${sample.file}` : null,
    ].filter(Boolean);
    return (
        <div className="text-xs">
            <span className="text-gray-400">{t(T.contenuDernierEvenement)} </span>
            <span className="text-gray-700">{parts.join(' — ')}</span>
        </div>
    );
}

export function ContentTags({tags, showCounts = true}: ContentTagsProps) {
    const traduire = useTraduire();
    if (!tags?.length) return null;
    return (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
            {tags.map((tag) => (
                <div key={tag.key} className="text-xs">
                    <span className="text-gray-400">
                        {traduire(TAG_LABELS[shortKey(tag.key)]) || shortKey(tag.key)} :{' '}
                    </span>
                    <span className="text-gray-700">
                        {tag.values
                            .map((v) => valueLabel(traduire, tag.key, v.value)
                                + (showCounts ? ` (${v.count})` : ''))
                            .join(', ')}
                    </span>
                </div>
            ))}
        </div>
    );
}
